import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl, UntypedFormGroup
} from '@angular/forms';
import { Router } from "@angular/router";
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { SessionStorage, SessionStorageService } from 'ngx-webstorage';
import { combineLatest, from, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { LocalizationService } from 'src/app/localization.service';
import { Navigation } from 'src/app/state/app.actions';
import { ApplicationState } from 'src/app/state/app.state';
import { environment } from 'src/environments/environment';
import { AppState } from '../../app.service';
import { AppSettings } from '../../app.settings';
import { CartService } from '../../cart/cart.service';
import { SharingServices } from '../../cms-editor/services/sharing-service';
import { IntegrationService } from '../../integration/integration.service';
import { LoginSharedService } from '../../login/login-shared.service';
import { LogoutService } from '../../logout/logout.service';
import { SharedInfoService } from '../../partsCatalog/sharedInfo.service';
import { ResourceLoaderService } from '../../resource-loader.service';
import { ToasterService } from '../../toaster/toaster.service';
import { FeatureChecker } from '../feature-checker.service';
import { NotificationService as GenericNotificationService } from '../generic-notification/notification.service';
import { DownloadEvent, SbomService } from '../sbom.service';
import { HeaderService } from './header.service';
import { NotificationService } from './notification.service';

@Component({
    selector: 'header',
    templateUrl: 'header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: false
})
export class HeaderComponent implements AfterViewInit, OnInit, OnDestroy {
    @Select(ApplicationState.popupMode)
    popupMode$: Observable<boolean>;

    @SessionStorage('USERID')
    private _userId: string;

    private integrationUserId: string;
    dropdownStates: Map<string, boolean> = new Map();

    public get userId(): string {
        return this.integrationUserId || this._userId;
    }

    public get isIntegrationFlow(): boolean {
        return !!this.integrationUserId;
    }

    public cnt: string;
    public cartLoading: boolean = true;
    public initLoading: boolean = false;

    @SessionStorage('esn')
    private esn: string;

    private subscription: Subscription = new Subscription();

    public showSearch: boolean = true;

    public storeUrl : string = `${AppSettings.PUBLICATIONS_STORE}`;
    public lang_img: string;
    public searchForm: UntypedFormGroup;
    public srchCriteria: any;
    public menu;
    public application;
    public placeholder = {
        title: 'Search ESN/ATSN/GSN/Part Number or Description across all products'
    }

    public adminView = `${AppSettings.ADMIN_VIEW}/applications`;

    public accessFlags = {
        dealerNewsLetterPage: false,
        isSuperAdmin: false,
        isAdmin: false,
        isCMSAdmin : false,
        showSurveyLink:false,
        isUploadUpfitAllowed: false
    };
    public isServiceToolsAdmin: boolean;

    constructor(private readonly storage: SessionStorageService,
        private readonly translate: TranslateService,
        private readonly logoutService: LogoutService,
        private readonly loginShared: LoginSharedService,
        private readonly router: Router,
        private readonly toasterService: ToasterService,
        private readonly notificationService: NotificationService,
        private readonly headerService: HeaderService,
        private readonly fb: UntypedFormBuilder,
        private readonly integration: IntegrationService,
        private readonly appService: AppState,
        protected sharing: SharingServices,
        private readonly _partsSharedService: SharedInfoService,
        private readonly _sbomService: SbomService,
        private readonly cartService: CartService,
        private readonly _resourceLoader: ResourceLoaderService,
        private readonly store: Store,
        private readonly featureChecker: FeatureChecker,
        private readonly genericNotificationService: GenericNotificationService,
        private readonly localizationService: LocalizationService) {
        this.lang_img = this.appService.getAssetsLink('/icons/language_change_icon.png', true);
        this.subscription.add(
            this.cartService.getCartItemsCount().subscribe({
                next: (cnt) => this.cnt = cnt + '',
                error: () => { }
            })
        );
        this.subscription.add(
            this._sbomService.loaderEvent$
                .pipe(filter(event => event?.type === 'error'))
                .subscribe({
                    next: (event) => {
                        let errMsg = 'Service BOM download has failed. Please try again later or contact help desk.';
                        this.translate.get(errMsg).subscribe({
                            next: (toast: string) => {
                                this.toasterService.pop({
                                    type: 'error',
                                    title: toast,
                                   config: { timeOut: 3000 }
                                });
                            },
                            error: () => { }
                        });
                    },
                    error: () => { }
                })
        );
        this.subscription.add(
            this.integration.getIntegrationUser().subscribe({
                next: userId => { this.integrationUserId = userId; },
                error: () => { }
            })
        );
        this.translate.setDefaultLang('en');
        this.createForm();
    }

    public getAssetsLink(path: string): string {
        return this.appService.getAssetsLink(path, true);
    }

    createForm() {
        this.searchForm = this.fb.group({
            searchValue: ['', checkSearchValidation]
        });
    }

    public ngAfterViewInit() {
        this._resourceLoader.loadResource({ src:'assets/js/common.js', type: 'script' }).subscribe({
            next: () => {},
            error: (err) => console.error(err)
        });
        this.sharing.publishToken.subscribe({
            next: (message:any) => {
                if(message && message.children != null){
                    this.menu = message.children;
                    this.application = message.name;
                }
            },
            error: (err) => console.error('Error subscribing to menu updates', err)
        });
    }

    public ngOnInit() {
        this.subscription.add(
            this.notificationService.notifications$.subscribe({
                next: (x) => this.toasterService.pop(x),
                error: (err) => console.error('Error subscribing to notifications', err)
            })
        );
        this.subscription.add(
            this.loginShared.checkFeatureAccess('DEALER_NEWS').subscribe({
                next: (flag) => { this.accessFlags.dealerNewsLetterPage = flag; },
                error: (err) => console.error('Error checking feature access for DEALER_NEWS', err)
            })
        );
        this.subscription.add(
            this.loginShared.checkFeatureAccess('Admin', 'adminservices').subscribe({
                next: (flag) => { this.accessFlags.isAdmin = flag; },
                error: (err) => console.error('Error checking feature access for Admin', err)
            })
        );
        this.subscription.add(
            this.loginShared.checkFeatureAccess('Super Admin', 'adminservices').subscribe({
                next: (flag) => { this.accessFlags.isSuperAdmin = flag; },
                error: (err) => console.error('Error checking feature access for Super Admin', err)
            })
        );
        this.subscription.add(
            this.loginShared.checkFeatureAccess('CMS_USER', 'IACDataServices').subscribe({
                next: (flag) => { this.accessFlags.isCMSAdmin = flag; },
                error: (err) => console.error('Error checking feature access for CMS_USER', err)
            })
        );
        this.subscription.add(
            this.headerService.registerSearchUpdates({
                next: (data) => {
                    if (data.func === "input") this.srchCriteria = data.input;
                },
                error: (err) => console.error('Error subscribing to search updates', err)
            })
        );

        this.subscription.add(this.loginShared.checkFeatureAccess('MST_ADMIN','mstwebservice').subscribe(flag => this.isServiceToolsAdmin = flag));
        this.subscription.add(this.loginShared.checkFeatureAccess('UPFITS_UPLOAD').subscribe(flag => this.accessFlags.isUploadUpfitAllowed = flag));

        this.sharing.publishToken.subscribe((message:any) => {
            if(message && message.children != null){
                this.menu = message.children;
                this.application = message.name;
            }
        });

        this.cartService.notifyCartLoaded(() => this.cartLoading = false, () => this.initLoading = true);

        this.storage.observe('USERID').subscribe({
            next: (newValue) => {
                    console.log('USER' + newValue);
            },
            error: (err) => console.error('Error observing USERID changes in session storage', err)
        });
        if (this._userId == null || this._userId == '') {
            this._userId = 'Login';
        }
        if (this.cnt == null || this.cnt == '') {
            this.cnt = '0';
        }
        this.genericNotificationService.loading$.subscribe({
            next: (event) => {
                if (!event.showLoading) {
                    from(this.featureChecker.applyFeature('SHOW_SURVEY_LINK')).subscribe({
                        next: (result: boolean) => {
                            this.accessFlags.showSurveyLink = result;
                        },
                        error: (err) => console.error('Error applying feature:', err)
                    });
                }
            },
            error: (err) => console.error('Error subscribing to loading events', err)
        });
    }

    public redirectToQsol(): void {
        const time = Date.now();
        let token = "";
        let urlToRedirect =  "";
        if(this.integration.data.vendor !== 'IAC' && this.integration.data.vendorCartId !== ''){
            token = this.integration.data.vendor + "|" + this.integration.data.vendorCartId + "|" + time;
            token =  btoa(token);
            urlToRedirect = `${AppSettings.QUICK_SERVE}/cola2/login/lnkmgmt/parts.html?tokenId=${token}&esn=${this.esn}
                &usertype=${btoa(this.integration.data.userType || '')}&transaction=${btoa(this.integration.data.transactionId || '')}`;
        }else{
            token = 'IAC|'+time;
            token =  btoa(token);
            if(this.esn)
                 urlToRedirect = `${AppSettings.QUICK_SERVE}/cola2/login/lnkmgmt/home.html?tokenId=${token}&esn=${this.esn}`;
            else
                urlToRedirect =  `${AppSettings.QUICK_SERVE_LOGIN}`;
        }

        window.open(urlToRedirect,"_blank");
    }

    public checkLogout(): boolean {
        if (this.userId != 'Login') {
            return true;
        }
        else {
            return false;
        }
    }

    public logout(): void {
        this.logoutService.killSession().then(logoutURL => {
            this.appService.processResult(null);
            window.location.href = logoutURL;
        });
    }

    public ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public switchLanguage(language: string) {
        this.localizationService.setLanguage(language);
    }

    public mouseEnter() {
        this.lang_img = this.appService.getAssetsLink('/icons/language_dark.png', true);
    }


    public mouseLeave() {
        this.lang_img = this.appService.getAssetsLink('/icons/language_change_icon.png', true);
    }

    public goToSearch() {

        this.showSearch = false;
        this.router.navigate(['/global-search/main/', this.searchForm.value.searchValue]);
        this.headerService.updateSearchView(this.srchCriteria);
        this.showSearch = true;

    }
    public gotoSavedEsn() {
        this.router.navigate(['/saved-esn']);
    }
    public goToSearchHelp() {
        this.router.navigate(['/global-search/help/']);
    }

    public gotoEditor() :void{
        this.router.navigate(['/cms-editor']);

    }

    cilckme(){
        window.open('https://www.cummins.com/support/product-registration', '_blank', 'noopener,noreferrer');
    }

    public async openHelpDeskPage() {
        this.store.dispatch(new Navigation.PopupRoute([environment.HELPDESK_URLS.PRODUCT], {
            windowTarget: 'help-desk-page'
        }
        ));
    }

    get showSbomMenu$(): Observable<boolean> {
        return combineLatest([
            this.loginShared.checkFeatureAccess('SHOW_SBOM') as Observable<boolean>,
            this._partsSharedService.currentEsnInfo$ as Observable<{ mbom: string }>
        ]).pipe(
            map(([showSbomMenu, esnInfo]: [boolean, { mbom: string }]) =>
                showSbomMenu  && (esnInfo.mbom === 'mbom' || esnInfo.mbom === 'legacy')
            )
        );
    }

    get esn$(): Observable<string> {
        return this._partsSharedService.currentEsnInfo$.pipe(map(esnInfo => esnInfo.esn));
    }

    get sbomLoaderEvents$(): Observable<DownloadEvent> {
        return this._sbomService.loaderEvent$;
    }

    public downloadSbom(event: MouseEvent) {
        this._sbomService.downloadSbom(this._partsSharedService);
        event.preventDefault();
    }

    get isLoggedIn(): boolean {
        return this.loginShared.isLoggedIn();
    }
}

function checkSearchValidation(input: UntypedFormControl) {
    let patt = new RegExp("^[0-9]+$");
    let dispAplhaNumericError: boolean = false;
    let dispDigitError: boolean = false;
    if (input.value != '' && input.value !== undefined) {

        if (patt.test(input.value)) {
            dispDigitError = true;
            if (input.value.length > 3) { return null; }

        } else if (input.value.match(/[a-z]/i)) {
            dispAplhaNumericError = true;
            if (input.value.length > 2) { return null; }
        }

        if (dispAplhaNumericError && dispDigitError === false) {
            return { needsAplhaNumeric: true };
        }

        if (dispDigitError && dispAplhaNumericError === false) {
            return { needsDigit: true };
        }
    }
}
